import React from 'react';
import {Atom, withSystem} from '@packages/systems/wfdl';
import {TBoolean, TText} from '@packages/systems/wfdl-ast';
import {createIntegratedAtom} from '@packages/utilities/integrated-types';
import {FEATURE_FLAGS} from '@packages/systems/feature-config/shared';
import {useHasSSRFeatureFlag} from '@packages/systems/render';
import type {LocaleItemContext} from '../types';
import {constructUrlForHreflang} from './utils/constructUrlForHreflang';
import {legacyConstructUrlForHreflang} from './utils/legacyConstructUrlForHreflang';

const Hreflangs = Atom.createRender(({data, context}) => {
  const d = data as Record<string, unknown>;
  const domain = d.domain as string | null | undefined;
  const hrefPrefixForHreflangLinks = d.hrefPrefixForHreflangLinks as
    | string
    | null
    | undefined;
  const hrefPrefixSameAsCanonicalUrl = d.hrefPrefixSameAsCanonicalUrl as
    | boolean
    | null
    | undefined;
  const globalCanonicalTag = d.globalCanonicalTag as string | null | undefined;
  const primaryPageCanonicalUrl = d.primaryPageCanonicalUrl as
    | string
    | null
    | undefined;
  const paginationQuery = d.paginationQuery as string | null | undefined;

  const locales: LocaleItemContext[] = context.get('locales') || [];

  const isUsingHreflangCanonicalTagUpdates = useHasSSRFeatureFlag(
    FEATURE_FLAGS.HREFLANG_CANONICAL_TAG_UPDATES
  );

  if (!domain || locales.length < 2) {
    return null;
  }

  return (
    <>
      {locales.map((locale) => {
        if (
          locale.noPageAvailable ||
          !locale.pageHref ||
          // We'll need to filter out entries that are
          // currently in draft status.
          (typeof locale.draft !== 'undefined' && locale.draft)
        ) {
          return null;
        }

        const constructUrlFunction = isUsingHreflangCanonicalTagUpdates
          ? constructUrlForHreflang
          : legacyConstructUrlForHreflang;

        let href = constructUrlFunction({
          domain,
          hrefPrefixForHreflangLinks,
          hrefPrefixSameAsCanonicalUrl,
          globalCanonicalTag,
          primaryPageCanonicalUrl,
          locale,
        });

        // Support pagination query params, regardless
        // of how this href was constructed.
        if (isUsingHreflangCanonicalTagUpdates && paginationQuery) {
          href = `${href}${paginationQuery}`;
        }

        return (
          <React.Fragment key={locale.tag}>
            {locale.primary && (
              <link rel="alternate" hrefLang="x-default" href={href} />
            )}
            <link rel="alternate" hrefLang={locale.tag} href={href} />
          </React.Fragment>
        );
      })}
    </>
  );
});

const config = createIntegratedAtom({
  render: Hreflangs,
  showEmpty: false,
  fields: {
    domain: withSystem({private: true}, TText),
    hrefPrefixForHreflangLinks: withSystem({private: true}, TText),
    hrefPrefixSameAsCanonicalUrl: withSystem({private: true}, TBoolean),
    globalCanonicalTag: withSystem({private: true}, TText),
    primaryPageCanonicalUrl: withSystem({private: true}, TText),
    paginationQuery: withSystem({private: true}, TText),
  },
});

export default config;
